// Currency formatting utilities

/**
 * Format number as Brazilian currency (BRL)
 * @param {number|string} value - The value to format
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') return 'R$ 0,00';
  
  const numValue = typeof value === 'string' ? parseFloat(value.replace(/[^\d,-]/g, '').replace(',', '.')) : value;
  
  if (isNaN(numValue)) return 'R$ 0,00';
  
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(numValue);
};

/**
 * Format number for display with thousand separators
 * @param {number|string} value - The value to format
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted number string
 */
export const formatNumber = (value, decimals = 2) => {
  if (value === null || value === undefined || value === '') return '0,00';
  
  const numValue = typeof value === 'string' ? parseFloat(value.replace(/[^\d,-]/g, '').replace(',', '.')) : value;
  
  if (isNaN(numValue)) return '0,00';
  
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(numValue);
};

/**
 * Parse Brazilian currency string to number
 * @param {string} currencyString - Currency string like "R$ 1.234,56"
 * @returns {number} Parsed number
 */
export const parseCurrency = (currencyString) => {
  if (!currencyString) return 0;
  
  // Remove R$, spaces, and replace dots and commas correctly
  const cleanString = currencyString
    .replace(/R\$\s?/g, '')
    .replace(/\./g, '')  // Remove thousand separators
    .replace(',', '.');  // Replace decimal comma with dot
  
  const parsed = parseFloat(cleanString);
  return isNaN(parsed) ? 0 : parsed;
};

/**
 * Format input value as user types (currency mask)
 * @param {string} value - Current input value
 * @returns {string} Formatted value with currency mask
 */
export const applyCurrencyMask = (value) => {
  if (!value) return '';
  
  // Remove all non-numeric characters
  const numericValue = value.replace(/[^\d]/g, '');
  
  if (!numericValue) return '';
  
  // Convert to number and add decimal places
  const number = parseInt(numericValue) / 100;
  
  return formatCurrency(number);
};

/**
 * Format percentage
 * @param {number|string} value - The value to format as percentage
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (value, decimals = 1) => {
  if (value === null || value === undefined || value === '') return '0%';
  
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(numValue)) return '0%';
  
  return new Intl.NumberFormat('pt-BR', {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(numValue / 100);
};

/**
 * Format compact number (like 1.2K, 3.4M)
 * @param {number} value - The value to format
 * @returns {string} Compact formatted number
 */
export const formatCompactNumber = (value) => {
  if (value === null || value === undefined) return '0';
  
  return new Intl.NumberFormat('pt-BR', {
    notation: 'compact',
    compactDisplay: 'short'
  }).format(value);
};

// Input masks for common formats
export const inputMasks = {
  /**
   * Apply phone mask (Brazilian format)
   * @param {string} value - Input value
   * @returns {string} Masked phone number
   */
  phone: (value) => {
    if (!value) return '';
    const numericValue = value.replace(/[^\d]/g, '');
    
    if (numericValue.length <= 10) {
      return numericValue.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }
    return numericValue.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  },

  /**
   * Apply CPF mask
   * @param {string} value - Input value
   * @returns {string} Masked CPF
   */
  cpf: (value) => {
    if (!value) return '';
    const numericValue = value.replace(/[^\d]/g, '');
    return numericValue.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  },

  /**
   * Apply CNPJ mask
   * @param {string} value - Input value
   * @returns {string} Masked CNPJ
   */
  cnpj: (value) => {
    if (!value) return '';
    const numericValue = value.replace(/[^\d]/g, '');
    return numericValue.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  },

  /**
   * Apply CEP mask
   * @param {string} value - Input value
   * @returns {string} Masked CEP
   */
  cep: (value) => {
    if (!value) return '';
    const numericValue = value.replace(/[^\d]/g, '');
    return numericValue.replace(/(\d{5})(\d{3})/, '$1-$2');
  }
};

export default {
  formatCurrency,
  formatNumber,
  parseCurrency,
  applyCurrencyMask,
  formatPercentage,
  formatCompactNumber,
  inputMasks
};